// Constants extracted from MyCalendarModule.js

import _ from "lodash";
import { IsReflectiveProjectDpTagEnabled } from "Utils/ACLStoreUtils";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";
import React from "react";
import { getDateMomentObject } from "Utils/DateTimeHelpers";
import {
  AiOutlined,
  AssessmentAddOutlined,
  CommunityOutlined,
  FaOutlined,
  JournalOutlined,
  LeLibraryOutlined,
  LeOutlined,
  QtOutlined,
  SaOutlined,
  UnitPlanOutlined,
  VideoOutlined,
  WorksheetClipboardOutlined,
} from "@toddle-edu/ds-icons";
import {
  ENABLE_ASSESSMENTS_IN_PYP,
  TODDLE_LIBRARY_PERM,
} from "Constants/permissionConstants";
import {
  ASSESSMENT_TYPE_AI_TUTOR,
  ASSESSMENT_TYPE_WORKSHEET,
  CREATE_AI_TUTOR,
} from "Constants/stringConstants";
import ACLStore from "lib/aclStore";

// Constants
export const NAME = "myCalendar";
export const UPDATE_STATE = "UPDATE_STATE" + " " + NAME;
export const UPDATE_DAILY_VIEW_STATE = "UPDATE_DAILY_VIEW_STATE" + " " + NAME;
export const UPDATE_VIEW_ONLY_TAGS_STATE =
  "UPDATE_VIEW_ONLY_TAGS_STATE" + " " + NAME;
export const UPDATE_FULL_SCREEN_VIEW_DATA =
  "UPDATE_FULL_SCREEN_VIEW_DATA" + " " + NAME;
export const SET_SELECTED_SYNC_CALENDAR =
  "SET_SELECTED_SYNC_CALENDAR" + " " + NAME;
export const MYP_PROJECT_TAGS = t => [
  {
    label: t("project:personal_project"),
    value: MY_CALENDAR_CONSTANTS.PERSONAL,
  },
  {
    label: t("project:community_project"),
    value: MY_CALENDAR_CONSTANTS.COMMUNITY,
  },
];
export const DP_PROJECT_TAGS = t => {
  const dpTags = [
    {
      label: t("project:tok_essay"),
      value: MY_CALENDAR_CONSTANTS.DP_TOK_ESSAY,
    },
    {
      label: t("project:tok_exhibition_sentence_case"),
      value: MY_CALENDAR_CONSTANTS.DP_TOK_EXHIBITION,
    },
    {
      label: t("project:extended_essay"),
      value: MY_CALENDAR_CONSTANTS.DP_EE,
    },
    {
      label: t("project:g4_project_lowercase"),
      value: MY_CALENDAR_CONSTANTS.DP_G4_PROJECT,
    },
    {
      label: t("project:dp_collaborative_sciences_project"),
      value: MY_CALENDAR_CONSTANTS.DP_COLLABORATIVE_SCIENCES_PROJECT,
    },
    {
      label: t("project:internal_external_assessment"),
      value: MY_CALENDAR_CONSTANTS.INTERNAL_EXTERNAL_ASSESSMENT,
    },
    {
      label: t("project:reflective_project"),
      value: MY_CALENDAR_CONSTANTS.CP_REFLECTIVE_PROJECT,
      perm: IsReflectiveProjectDpTagEnabled(),
    },
  ];
  const filteredDpTags = _.filter(dpTags, tag => _.get(tag, "perm", true));
  return filteredDpTags;
};
export const ALL_PROJECT_TAGS = t => [
  ...DP_PROJECT_TAGS(t),
  ...MYP_PROJECT_TAGS(t),
];
export const INITIAL_CONTENT_TYPES = [
  MY_CALENDAR_CONSTANTS.ASSESSMENT,
  MY_CALENDAR_CONSTANTS.RECURRING_MEETING,
  MY_CALENDAR_CONSTANTS.SCHOOL_CALENDAR,
];
export const CLASS_BOUND_CONTENT_TYPES = [MY_CALENDAR_CONSTANTS.ASSESSMENT];
export const CLASS_INDEPENDENT_CONTENT_TYPES = _.difference(
  INITIAL_CONTENT_TYPES,
  CLASS_BOUND_CONTENT_TYPES
);
export const UPDATE_CALENDAR_SYNC_MODAL_STATE =
  "UPDATE_CALENDAR_SYNC_MODAL_STATE" + " " + NAME;
export const MICROSOFT_CALENDAR = "MICROSOFT_CALENDAR";
export const GOOGLE_CALENDAR = "GOOGLE_CALENDAR";
export const UPDATE_EVENT_MODALV2_STATE = `UPDATE_EVENT_MODALV2_STATE ${NAME}`;
export const UPDATE_WEEKLY_REPEAT_EVENT_SETTINGS = `UPDATE_WEEKLY_REPEAT_EVENT_SETTINGS ${NAME}`;
export const UPDATE_MONTHLY_SET_POSITIONS_REPEAT_EVENT_SETTINGS = `UPDATE_MONTHLY_SET_POSITIONS_REPEAT_EVENT_SETTINGS ${NAME}`;
export const UPDATE_MONTHLY_DAY_POSITIONS_REPEAT_EVENT_SETTINGS = `UPDATE_MONTHLY_DAY_POSITIONS_REPEAT_EVENT_SETTINGS ${NAME}`;
export const UPDATE_DAILY_REPEAT_EVENT_SETTINGS = `UPDATE_DAILY_REPEAT_EVENT_SETTINGS ${NAME}`;
export const RESET_EVENT_MODALV2_STATE = `RESET_EVENT_MODALV2_STATE ${NAME}`;

export const VIEW_FILTER_MAPPING = {
  AGENDA: "Agenda",
  DAILY: "Daily",
};

export const CALENDAR_TYPES = {
  GLOBAL_HEADER_CALENDAR: "GLOBAL_HEADER_CALENDAR",
  MASTER_CALENDAR: "MASTER_CALENDAR",
  CLASS_CALENDAR: "CLASS_CALENDAR",
};

export const ORGANIZATION_PREFERENCE_KEY_PREFIX = "ORGANIZATION_";
export const CCH_SELECTED_CURRICULUMPROGRAM_KEY =
  "CCH_SELECTED_CURRICULUMPROGRAM";
export const DEFAULT_REMINDER_VALUE_MAPPING = {
  scheduleTime: 10,
  sentVia: ["IN_APP", "PUSH"],
};

export const ENTITY_TYPE = {
  GRADES: "GRADES",
  CLASSES: "CLASSES",
  CUSTOM: "CUSTOM",
  STAFF_MEMBERS: "STAFF_MEMBERS",
  STAFF: "STAFF",
  STUDENTS: "STUDENTS",
  STUDENT: "STUDENT",
  PARENTS: "PARENTS",
  PARENT: "PARENT",
};

export const PARTICIPANT_USER_TYPES = {
  STAFF: "common:staff_member_other",
  STUDENT: "common:student_plural",
  FAMILY_MEMBER: "common:family_member_plural",
  PARENT: "common:family_member_plural",
};

export const USER_TYPE_COUNT_FIELDS = {
  STAFF: "staffCount",
  STUDENT: "studentCount",
  FAMILY_MEMBER: "familyCount",
  PARENT: "familyCount",
};

export const INITIAL_PARTICIPANT_SELECTIONS = {
  [ENTITY_TYPE.GRADES]: [],
  [ENTITY_TYPE.CLASSES]: [],
  [ENTITY_TYPE.CUSTOM]: [],
};

export const defaultEventDate = getDateMomentObject();

export const INITIAL_EVENT_MODALV2_STATE = {
  eventTitle: "",
  eventType: "",
  // BE `CALENDAR_EVENT_ITEM_TYPE_ENUM`. Distinct from `eventType` above, which
  // holds the school-calendar-tag ID selected in the dropdown.
  calendarEventItemType: "",
  startDate: defaultEventDate,
  endDate: defaultEventDate,
  isAllDayEvent: false,
  participants: INITIAL_PARTICIPANT_SELECTIONS,
  location: "",
  description: "",
  attachments: [],
  reminders: [],
  initialAttachments: [],
  eventUID: "",
  eventId: null,
  sharedWith: ["STUDENT", "STAFF", "FAMILY_MEMBER"],
  entityType: "",
  totalParticipantUserCount: 0,
  isRepeatEvent: false,
  skipNonOperationalDays: false,
};

export const PYP_CREATE_OPTIONS_V2 = [
  {
    label: "common:create",
    key: "create",
    isItemGroup: true,
    options: [
      {
        label: "common:video_meeting_lower",
        key: "SCHEDULE_VIDEO_CALL",
        icon: <VideoOutlined variant={"subtle"} />,
        hasTermDependency: false,
      },
      {
        label: "",
        key: "",
        icon: null,
        hasTermDependency: false,
        isDivider: true, // This is used to create a divider in the dropdown
      },
      {
        label: "common:quick_task",
        key: "CREATE_QUICK_TASK",
        value: "qt",
        icon: <QtOutlined variant={"subtle"} />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:le_label",
        key: "CREATE_LE",
        value: "le",
        icon: <LeOutlined variant={"subtle"} />,
        perm: "TeacherPortal:CreateClassroomLE",
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "classRoom:formative_assessment",
        key: "CREATE_FA",
        value: "fmt",
        icon: <FaOutlined variant="subtle" />,
        perm: ENABLE_ASSESSMENTS_IN_PYP,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "classRoom:summative_assessment",
        key: "CREATE_SA",
        value: "smt",
        icon: <SaOutlined variant="subtle" />,
        perm: ENABLE_ASSESSMENTS_IN_PYP,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:worksheet",
        key: "CREATE_WORKSHEET",
        value: ASSESSMENT_TYPE_WORKSHEET,
        icon: <WorksheetClipboardOutlined variant={"subtle"} />,
        perm: "FeatureFlag:ClassroomWorksheet",
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:ai_tutor",
        key: CREATE_AI_TUTOR,
        value: ASSESSMENT_TYPE_AI_TUTOR,
        icon: <AiOutlined variant={"subtle"} />,
        hasTermDependency: true,
        // perm: ENABLE_AI_TUTOR,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
    ],
  },
];

const PYP_IMPORT_OPTIONS_GROUP_V2 = [
  {
    label: "common:import_from",
    key: "Assign",
    isItemGroup: true,
    options: [
      {
        label: "common:unit",
        key: ACLStore.can(ENABLE_ASSESSMENTS_IN_PYP) ? "UNIT" : "IMPORT_LE",
        icon: <UnitPlanOutlined variant={"subtle"} />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:import_from_label_no_grading_periods_tooltip",
      },
      {
        label: "common:library",
        key: "IMPORT_LE_FROM_LE_LIBRARY",
        icon: <LeLibraryOutlined variant={"subtle"} />,
        perm: "TeacherPortal:LELibrary",
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:import_from_label_no_grading_periods_tooltip",
      },
      {
        label: "common:community",
        key: "IMPORT_LE_FROM_COMMUNITY",
        icon: <CommunityOutlined variant={"subtle"} />,
        perm: "TeacherPortal:Community",
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:import_from_label_no_grading_periods_tooltip",
      },
      {
        label: "common:toddle_library",
        key: "IMPORT_FROM_TODDLE_LIBRARY",
        icon: <JournalOutlined variant={"subtle"} />,
        moduleSettingControlParam: TODDLE_LIBRARY_PERM,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:import_from_label_no_grading_periods_tooltip",
      },
    ],
  },
];

export const ASSIGNMENT_CREATE_OPTIONS_NEW_V2 = [
  ...PYP_CREATE_OPTIONS_V2,
  ...PYP_IMPORT_OPTIONS_GROUP_V2,
];

export const MYP_CREATE_OPTIONS_V2 = [
  {
    label: "common:create",
    key: "create",
    isItemGroup: true,
    options: [
      {
        label: "common:video_meeting_lower",
        key: "SCHEDULE_VIDEO_CALL",
        icon: <VideoOutlined variant={"subtle"} />,
        hasTermDependency: false,
      },
      {
        label: "",
        key: "",
        icon: null,
        hasTermDependency: false,
        isDivider: true, // This is used to create a divider in the dropdown
      },
      {
        label: "common:quick_task",
        key: "CREATE_QUICK_TASK",
        value: "qt",
        icon: <QtOutlined variant={"subtle"} />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "classRoom:formative_assessment",
        key: "CREATE_FA",
        value: "fmt",
        icon: <FaOutlined variant="subtle" />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "classRoom:summative_assessment",
        key: "CREATE_SA",
        value: "smt",
        icon: <SaOutlined variant="subtle" />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:le_label",
        key: "CREATE_LE",
        value: "le",
        icon: <LeOutlined variant="subtle" />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:worksheet",
        key: "CREATE_WORKSHEET",
        value: ASSESSMENT_TYPE_WORKSHEET,
        icon: <WorksheetClipboardOutlined variant={"subtle"} />,
        perm: "FeatureFlag:ClassroomWorksheet",
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:ai_tutor",
        key: CREATE_AI_TUTOR,
        value: ASSESSMENT_TYPE_AI_TUTOR,
        icon: <AiOutlined variant={"subtle"} />,
        hasTermDependency: true,
        // perm: ENABLE_AI_TUTOR,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
    ],
  },
];

const MYP_IMPORT_OPTIONS_GROUP = {
  label: "common:import_from",
  key: "Assign",
  isItemGroup: true,
  options: [
    {
      label: "common:library",
      key: "IMPORT_LE_FROM_LE_LIBRARY",
      icon: <LeOutlined variant={"subtle"} />,
      perm: "TeacherPortal:LELibraryMyp",
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
    {
      label: "common:unit",
      key: "UNIT",
      icon: <UnitPlanOutlined variant={"subtle"} />,
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
    {
      label: "common:toddle_library",
      key: "IMPORT_FROM_TODDLE_LIBRARY",
      icon: <JournalOutlined variant={"subtle"} />,
      moduleSettingControlParam: TODDLE_LIBRARY_PERM,
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
  ],
};

export const MYP_ASSIGNMENT_OPTIONS_NEW_V2 = [
  ...MYP_CREATE_OPTIONS_V2,
  MYP_IMPORT_OPTIONS_GROUP,
];

export const UBD_CREATE_OPTIONS_V2 = [
  {
    label: "common:create",
    key: "create",
    isItemGroup: true,
    options: [
      {
        label: "common:video_meeting_lower",
        key: "SCHEDULE_VIDEO_CALL",
        icon: <VideoOutlined variant={"subtle"} />,
        hasTermDependency: false,
      },
      {
        label: "",
        key: "",
        icon: null,
        hasTermDependency: false,
        isDivider: true, // This is used to create a divider in the dropdown
      },
      {
        label: "common:quick_task",
        key: "CREATE_QUICK_TASK",
        value: "QUICK_TASK",
        icon: <QtOutlined variant={"subtle"} />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:assessment",
        key: "CREATE_ASSESSMENT",
        value: "ASSESSMENT",
        icon: <AssessmentAddOutlined variant={"subtle"} />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:le_label",
        key: "CREATE_LE",
        value: "LEARNING_ENGAGEMENT",
        icon: <LeOutlined variant={"subtle"} />,
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:worksheet",
        key: "CREATE_WORKSHEET",
        value: ASSESSMENT_TYPE_WORKSHEET,
        icon: <WorksheetClipboardOutlined variant={"subtle"} />,
        perm: "FeatureFlag:ClassroomWorksheet",
        hasTermDependency: true,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
      {
        label: "common:ai_tutor",
        key: CREATE_AI_TUTOR,
        value: ASSESSMENT_TYPE_AI_TUTOR,
        icon: <AiOutlined variant={"subtle"} />,
        hasTermDependency: true,
        // perm: ENABLE_AI_TUTOR,
        noTermsDisabledTooltip:
          "classRoom:assessment_creation_no_grading_periods_tooltip_with_label_plural",
      },
    ],
  },
];

const UBD_IMPORT_OPTIONS_GROUP = {
  label: "common:import_from",
  key: "Assign",
  isItemGroup: true,
  options: [
    {
      label: "common:library",
      key: "IMPORT_LE_FROM_LE_LIBRARY",
      icon: <LeLibraryOutlined variant={"subtle"} />,
      perm: "TeacherPortal:LELibraryUbd",
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
    {
      label: "common:unit",
      key: "UNIT",
      icon: <UnitPlanOutlined variant={"subtle"} />,
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
    {
      label: "common:toddle_library",
      key: "IMPORT_FROM_TODDLE_LIBRARY",
      icon: <JournalOutlined variant={"subtle"} />,
      moduleSettingControlParam: TODDLE_LIBRARY_PERM,
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
  ],
};

export const UBD_ASSIGNMENT_OPTIONS_NEW_V2 = [
  ...UBD_CREATE_OPTIONS_V2,
  UBD_IMPORT_OPTIONS_GROUP,
];

export const DP_IMPORT_OPTIONS_GROUP = {
  label: "common:import_from",
  key: "Assign",
  isItemGroup: true,
  options: [
    {
      label: "common:library",
      key: "IMPORT_LE_FROM_LE_LIBRARY",
      icon: <LeOutlined variant={"subtle"} />,
      perm: "TeacherPortal:LELibraryMyp",
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
    {
      label: "common:unit",
      key: "UNIT",
      icon: <UnitPlanOutlined variant={"subtle"} />,
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
    {
      label: "common:toddle_library",
      key: "IMPORT_FROM_TODDLE_LIBRARY",
      icon: <JournalOutlined variant={"subtle"} />,
      moduleSettingControlParam: TODDLE_LIBRARY_PERM,
      hasTermDependency: true,
      noTermsDisabledTooltip:
        "classRoom:import_from_label_no_grading_periods_tooltip",
    },
  ],
};

export const DP_ASSIGNMENT_OPTIONS_NEW_V2 = [
  ...MYP_CREATE_OPTIONS_V2,
  DP_IMPORT_OPTIONS_GROUP,
];

export const THIRD_PARTY_SERVICE_TYPE = {
  ZOOM: "ZOOM",
  GOOGLE: "GOOGLE",
  MICROSOFT: "MICROSOFT",
};
